import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import CiteSummary from "@/components/blog/CiteSummary";

const BLOG_POSTING_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": "What CLIA Requires for Lab SOP Management",
  "url": "https://labcomplyx.com/blog/lab-sop-management",
  "mainEntityOfPage": "https://labcomplyx.com/blog/lab-sop-management",
  "datePublished": "2026-07-15",
  "dateModified": "2026-07-15",
  "author": { "@id": "https://labcomplyx.com/#founder" },
  "publisher": { "@id": "https://labcomplyx.com/#organization" },
};

export default function LabSOPManagement() {
  return (
    <>
      <SEO
        title="What CLIA Requires for Lab SOP Management | LabComplyx Blog"
        description="A practical breakdown of CLIA's SOP documentation requirements — what inspectors look for, common gaps, and how to ensure your lab is covered."
        canonical="https://labcomplyx.com/blog/lab-sop-management"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BLOG_POSTING_SCHEMA)}</script>
      </Helmet>
      <div className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/blog" className="text-xs text-muted-foreground hover:text-primary transition-colors">← Back to Blog</Link>
          </div>
          <span className="inline-block text-xs font-medium text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">SOP · Compliance</span>
          <h1 className="text-4xl font-bold text-foreground mb-5">What CLIA Requires for Lab SOP Management</h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-10">
            CLIA's SOP requirements are specific, and inspectors know exactly what to look for. Here's a plain-language breakdown of what your lab needs — and the most common places labs fall short.
          </p>

          <CiteSummary
            heading="TL;DR — CLIA SOP Requirements"
            items={[
              "Under 42 CFR 493.1251, CLIA requires written procedures for all phases of testing: pre-analytic, analytic, and post-analytic.",
              "SOPs must be reviewed and approved by the laboratory director at least annually, with dated signatures.",
              "Discontinued procedures must be retained for at least 2 years — version history and change documentation are regulatory requirements, not options.",
              "The most common SOP inspection failures: references to retired equipment, missing annual review signatures, procedures that don't match actual practice, and new tests without written procedures.",
            ]}
          />

          <div className="prose prose-sm max-w-none space-y-8 text-foreground">
            <section>
              <h2 className="text-xl font-bold mb-3">What CLIA actually requires</h2>
              <p className="text-muted-foreground leading-relaxed">Under 42 CFR 493.1251, CLIA requires labs to have written procedures for all phases of testing — pre-analytic, analytic, and post-analytic. These procedures must be available to staff, reviewed at defined intervals, and updated when the test procedure changes.</p>
            </section>
            <section>
              <h2 className="text-xl font-bold mb-3">The review and retention requirement</h2>
              <p className="text-muted-foreground leading-relaxed">SOPs must be reviewed and approved by the laboratory director at least annually. When a procedure is discontinued, the lab must retain documentation for at least 2 years. This means version history and change documentation are not optional — they are regulatory requirements.</p>
            </section>
            <section>
              <h2 className="text-xl font-bold mb-3">Where labs most commonly fail SOP inspections</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>SOPs reference equipment that has been replaced or retired</li>
                <li>Annual review signatures are missing or undated</li>
                <li>Procedures don't reflect actual lab practice (staff use informal shortcuts)</li>
                <li>New tests were implemented without a written procedure</li>
                <li>SOPs are stored in a location staff can't easily access</li>
              </ul>
            </section>
            <section>
              <h2 className="text-xl font-bold mb-3">A practical approach to continuous compliance</h2>
              <p className="text-muted-foreground leading-relaxed">The most effective strategy is to treat SOP review as a scheduled, documented workflow — not a one-time event before inspection. Assign ownership to specific procedures, set calendar reminders for review cycles, and document every review with a dated signature.</p>
            </section>
          </div>

          <div className="mt-12 p-7 bg-muted/30 border border-border rounded-xl">
            <h3 className="font-semibold text-foreground mb-3">Need help getting your SOPs audit-ready?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">LabComplyx provides SOP management support for regulated labs — from drafting and review to versioning and lifecycle tracking. Book a free compliance review to start.</p>
            <Link to="/validation-intake"><Button className="gap-2">Book a Free Compliance Review <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>

          <div className="mt-8 pt-8 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-3">Related</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/solutions/sop-management" className="text-xs font-medium text-primary hover:underline">SOP Management Solution →</Link>
              <Link to="/blog/clia-gap-audit" className="text-xs font-medium text-primary hover:underline">What a CLIA Gap Audit Finds →</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}